/**
 * Resolves the browser's IANA time zone identifier (e.g., "America/Toronto" or "America/Mexico_City").
 *
 * Context & Purpose:
 * Back-end services run on UTC servers. When date presets (such as "Monthly analytics") are evaluated
 * on a UTC server clock, month boundaries roll over early in western time zones (e.g. at 8:00 PM in Toronto
 * or 6:00 PM in Mexico City on the final day of the month).
 *
 * Passing this value alongside request payloads allows the backend to align day boundaries and "this month"
 * queries with the user's actual local calendar day.
 *
 * @returns The IANA timezone string if resolved by the browser environment, or `undefined` if resolution fails or is unsupported.
 */
export function browserTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && tz.trim() ? tz : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A request body with the browser's zone added as `timezone`, for endpoints that
 * work date periods out on the server (dashboard widgets, team won amounts).
 *
 * Only a plain object is extended. Anything else (a string, an array, FormData,
 * a class instance) is returned untouched, because spreading it would corrupt
 * the payload. The caller's object is never mutated, and a `timezone` the caller
 * already set wins. When the browser cannot name its zone the field is
 * undefined, JSON drops it, and the server keeps its old behaviour.
 */
export function withBrowserTimeZone<T>(body: T): T {
  const plain = !!body && typeof body === 'object' && Object.getPrototypeOf(body) === Object.prototype;
  return plain ? ({ timezone: browserTimeZone(), ...(body as object) } as T) : body;
}
